import { useTranslation } from 'react-i18next'
import { useInView } from '@/hooks/use-in-view'
import { PenTool, Settings, Play } from 'lucide-react'

interface StepProps {
  number: number
  icon: React.ReactNode
  titleKey: string
  descriptionKey: string
  delay: number
  isLast: boolean
}

function Step({ number, icon, titleKey, descriptionKey, delay, isLast }: StepProps) {
  const { t } = useTranslation('landing')
  const [ref, inView] = useInView<HTMLDivElement>({ threshold: 0.3 })

  return (
    <div
      ref={ref}
      className={`relative flex flex-col items-center text-center space-y-4 transition-all duration-700 ${
        inView ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="relative">
        <div className="w-16 h-16 rounded-full bg-indigo-500/20 border-2 border-indigo-500/50 flex items-center justify-center text-2xl font-bold text-indigo-400 glow-primary">
          {number}
        </div>
        <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white shadow-lg">
          {icon}
        </div>
      </div>

      <div className="space-y-2 max-w-xs">
        <h3 className="text-2xl font-semibold text-white">{t(titleKey)}</h3>
        <p className="text-gray-400 leading-relaxed">{t(descriptionKey)}</p>
      </div>

      {!isLast && (
        <div className="hidden lg:block absolute top-8 left-[calc(50%+4rem)] w-[calc(100%-2rem)] h-0.5 bg-gradient-to-r from-indigo-500/50 to-transparent" />
      )}
    </div>
  )
}

const steps = [
  { icon: <PenTool className="w-5 h-5" />, titleKey: 'howItWorks.step1.title', descriptionKey: 'howItWorks.step1.description' },
  { icon: <Settings className="w-5 h-5" />, titleKey: 'howItWorks.step2.title', descriptionKey: 'howItWorks.step2.description' },
  { icon: <Play className="w-5 h-5" />, titleKey: 'howItWorks.step3.title', descriptionKey: 'howItWorks.step3.description' },
]

export function HowItWorks() {
  const { t } = useTranslation('landing')
  const [ref, inView] = useInView<HTMLDivElement>({ threshold: 0.1 })

  return (
    <section className="relative py-24 px-4 bg-gradient-to-b from-transparent to-gray-900/30">
      <div className="max-w-6xl mx-auto">
        <div
          ref={ref}
          className={`text-center max-w-3xl mx-auto mb-20 space-y-4 transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white">{t('howItWorks.title')}</h2>
          <p className="text-lg text-gray-400">{t('howItWorks.subtitle')}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12 lg:gap-8">
          {steps.map((step, index) => (
            <Step
              key={step.titleKey}
              number={index + 1}
              icon={step.icon}
              titleKey={step.titleKey}
              descriptionKey={step.descriptionKey}
              delay={index * 150}
              isLast={index === steps.length - 1}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
